import { defaultLabels } from './questionDefaults';
import type { Field } from './types';

export const newQuestionItemId = () => crypto.randomUUID();

/** Migrate missing identities at an editor write boundary, never on public reads.
 * Leave malformed existing IDs intact so validation can reject them.
 */
export function ensureQuestionIds(field: Field, createId = newQuestionItemId): Field {
  let next = field;
  if (['choice', 'checkboxes', 'dropdown'].includes(field.type) && field.optionIds === undefined) {
    next = { ...next, options: field.options?.length ? field.options : [''], optionIds: defaultLabels(field.options, 'Option').map(() => createId()) };
  }
  if (['choice_grid', 'checkbox_grid'].includes(field.type)) {
    const grid = field.grid ?? { rows: [''], columns: [''] };
    if (grid.rowIds === undefined || grid.columnIds === undefined) next = { ...next, grid: {
      ...grid,
      rows: grid.rows.length ? grid.rows : [''],
      columns: grid.columns.length ? grid.columns : [''],
      rowIds: grid.rowIds ?? defaultLabels(grid.rows, 'Row').map(() => createId()),
      columnIds: grid.columnIds ?? defaultLabels(grid.columns, 'Column').map(() => createId()),
    } };
  }
  return next;
}

/** Copies receive new question, option, row, and column identities. */
export function copyQuestion(field: Field, id = crypto.randomUUID().slice(0, 8)): Field {
  const copy = structuredClone(ensureQuestionIds(field));
  copy.id = id;
  if (copy.optionIds) copy.optionIds = copy.optionIds.map(() => newQuestionItemId());
  if (copy.grid?.rowIds) copy.grid.rowIds = copy.grid.rowIds.map(() => newQuestionItemId());
  if (copy.grid?.columnIds) copy.grid.columnIds = copy.grid.columnIds.map(() => newQuestionItemId());
  return copy;
}

/** Legacy arrays are optional; provided arrays must match their labels exactly. */
export function validQuestionItemIds(ids: unknown, count: number): boolean {
  return ids === undefined || (Array.isArray(ids) && ids.length === count &&
    ids.every(id => typeof id === 'string' && id.length > 0 && id.length <= 100 && id.trim() === id) &&
    new Set(ids).size === ids.length);
}
